import {
  AgCartesianAxisOptions,
  AgCartesianSeriesOptions,
  AgChartOptions,
} from 'ag-charts-enterprise';
import { AgCharts } from 'ag-charts-react';
import { useMemo } from 'react';
import { TFunction, useTranslation } from 'translations/hooks';
import { getAgChartLocale } from 'translations/locale';

import { useSingleGraphReferenceContext } from 'business/providers/graph-reference';
import {
  graphKeys,
  graphWidth,
  graphHeight,
  mobileGraphWidth,
  mobileGraphHeight,
  graphLegendHeight,
} from 'business/steering/pages/table-page/components/chart/steering-graph/constants';
import { useCircle } from 'business/steering/pages/table-page/components/chart/steering-graph/hooks/use-circle-points';
import { GraphKeys } from 'business/steering/pages/table-page/components/chart/steering-graph/types';
import { useSteeringChartFilters } from 'business/steering/providers/steering-chart-filters-provider';
import { GetSteeringDataQuery } from 'generated/graphql';
import { useChartResize } from 'technical/chart-resize';
import { createNumberFormatter } from 'technical/format/number';
import { useMediaType } from 'technical/media/hooks';
import { isUndefinedOrNull } from 'technical/utils/is-null-or-undefined';

interface SteeringGraphProps {
  data: GetSteeringDataQuery['parameter_ring'];
}

const getAxes = (
  scale: number,
  selectedPosition: keyof typeof graphKeys,
  circleKeys: { top: GraphKeys<string>; bot: GraphKeys<string> },
  t: TFunction,
) =>
  [
    {
      type: 'number',
      position: 'bottom',
      keys: [
        graphKeys[selectedPosition].abscissa,
        circleKeys.top.abscissa,
        circleKeys.bot.abscissa,
      ],
      min: -scale,
      max: scale,
      crosshair: {
        snap: false,
      },
      nice: false,
      title: {
        text: t('steering.graph.axes.horizontal'),
      },
    },
    {
      type: 'number',
      position: 'left',
      keys: [
        graphKeys[selectedPosition].ordinate,
        circleKeys.top.ordinate,
        circleKeys.bot.ordinate,
      ],
      min: -scale,
      max: scale,
      crosshair: {
        snap: false,
      },
      nice: false,
      title: {
        text: t('steering.graph.axes.vertical'),
        spacing: 0,
      },
    },
  ] satisfies AgCartesianAxisOptions[];

const getSerie = (
  selectedPosition: keyof typeof graphKeys,
  t: TFunction,
  numberFormatter: Intl.NumberFormat,
) => {
  const serieTitle = {
    rear: t('steering.filters.position.rear'),
    front: t('steering.filters.position.front'),
    cuttingWheel: t('steering.filters.position.cuttingWheel'),
  };

  return [
    {
      type: 'scatter',
      xKey: graphKeys[selectedPosition].abscissa,
      yKey: graphKeys[selectedPosition].ordinate,
      title: serieTitle[selectedPosition],
      tooltip: {
        renderer: (params) => {
          return {
            data: [
              {
                label: t('steering.graph.tooltip.ringNumber'),
                value: `${params.datum.ringNumber}`,
              },
              {
                label: t('steering.graph.tooltip.xKey', {
                  context: params.xKey,
                }),
                value: `${numberFormatter.format(params.datum[params.xKey])}`,
              },
              {
                label: t('steering.graph.tooltip.yKey', {
                  context: params.yKey,
                }),
                value: `${numberFormatter.format(params.datum[params.yKey])}`,
              },
            ],
          };
        },
      },
      id: 'selectedPosition',
    },
  ] satisfies AgCartesianSeriesOptions[];
};

export const SteeringGraph = ({ data }: SteeringGraphProps) => {
  const { t, language } = useTranslation();
  const { ref } = useSingleGraphReferenceContext();
  const filters = useSteeringChartFilters();

  const { isMobile } = useMediaType();

  const selectedPosition = filters.position ?? 'rear';
  const tolerance = filters.tolerance ?? 50;
  const scale = filters.scale ?? 50;

  const { circleSeries, circleAxeKeys } = useCircle(tolerance);

  const filteredData = useMemo(() => {
    return data.filter(
      (item) =>
        !isUndefinedOrNull(item[graphKeys[selectedPosition].abscissa]) &&
        !isUndefinedOrNull(item[graphKeys[selectedPosition].ordinate]),
    );
  }, [data, selectedPosition]);

  const integerFormatter = createNumberFormatter(language, 0);

  const chartOptions: AgChartOptions = {
    data: filteredData,
    axes: getAxes(scale, selectedPosition, circleAxeKeys, t),
    series: [
      ...getSerie(selectedPosition, t, integerFormatter),
      ...circleSeries,
    ],
    animation: { enabled: false },
    legend: {
      enabled: true,
      toggleSeries: false,
      maxHeight: graphLegendHeight,
    },
    locale: {
      localeText: getAgChartLocale(language),
    },
    ...(isMobile
      ? {
          width: mobileGraphWidth,
          height: mobileGraphHeight,
          touch: { dragAction: 'drag' },
          zoom: {
            enabled: true,
            axes: 'xy',
            autoScaling: { enabled: false },
            enableAxisDragging: false,
            enableTwoFingerZoom: true,
            buttons: {
              enabled: false,
            },
          },
          contextMenu: {
            enabled: false,
          },
        }
      : {
          width: graphWidth,
          height: graphHeight,
        }),
  };

  useChartResize(ref, true, graphLegendHeight, 550);

  return <AgCharts options={chartOptions} ref={ref} />;
};
